/* =========================================================================
 * Lodging module — club gating.
 *
 * The Lodging vertical (hotel-style stays: rooms, rates, housekeeping) is a
 * gated sibling of the Polo module. It should only surface for clubs that
 * actually run lodging.
 *
 * Gate source (in priority order):
 *   1. The backend `club.is_lodging_club` flag, returned on getMe / login and
 *      persisted in the cached `user` payload (localStorage "user"). This is
 *      the source of truth once the club is flagged in the DB.
 *   2. An explicit club-id allowlist — covers SUPER_ADMIN, whose active club
 *      is chosen via localStorage "Club_id" and whose full club object (with
 *      the flag) is not embedded in the cached `user`. Empty for now — no
 *      club is flagged yet.
 *   3. An optional REACT_APP_LODGING_ENABLED env flag so staging / demos /
 *      local dev can force the module on everywhere.
 * ========================================================================= */

/**
 * Clubs explicitly opted into the Lodging vertical. Unfound is club id 150.
 * This allowlist is what makes the module visible to SUPER_ADMIN, whose active
 * club comes from localStorage "Club_id" and whose cached `user` payload never
 * carries the selected club's is_lodging_club flag (same mechanism as polo).
 */
const LODGING_ENABLED_CLUBS: number[] = [150];

/** Force-enable Lodging everywhere via env (staging, demos, local dev). */
const LODGING_ENV_ENABLED =
  String(process.env.REACT_APP_LODGING_ENABLED || "").toLowerCase() === "true";

function readCurrentUser(): any {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

function readCurrentClubId(): number | null {
  try {
    const user = readCurrentUser();
    const selectedClubId = Number(localStorage.getItem("Club_id"));

    if (user?.role?.name === "SUPER_ADMIN" && Number.isInteger(selectedClubId)) {
      return selectedClubId;
    }

    const clubId = Number(user?.club_id ?? user?.club?.id);
    return Number.isInteger(clubId) ? clubId : null;
  } catch {
    return null;
  }
}

/** Read the backend `is_lodging_club` flag from the cached login/club payload. */
function readIsLodgingFlag(): boolean {
  try {
    const user = readCurrentUser();
    // The flag lives on the club object (`club.is_lodging_club`); tolerate a
    // top-level copy too in case a future getMe shape hoists it.
    return Boolean(user?.club?.is_lodging_club ?? user?.is_lodging_club);
  } catch {
    return false;
  }
}

export function isLodgingClub(): boolean {
  // 1. Backend-driven flag (source of truth once the club is flagged).
  if (readIsLodgingFlag()) return true;
  // 2. env force-on for staging / demos / local dev.
  if (LODGING_ENV_ENABLED) return true;
  // 3. Fallback allowlist (covers SUPER_ADMIN + not-yet-flagged clubs).
  const clubId = readCurrentClubId();
  return clubId != null && LODGING_ENABLED_CLUBS.includes(clubId);
}

/**
 * Clubs that run Lodging *instead of* the general platform: the app switcher
 * shows Lodging only (no Operations / Trips / Restaurant) and every admin
 * route outside the Lodging module bounces to /lodging. None exist yet.
 */
const LODGING_ONLY_CLUBS: number[] = [];

function isSuperAdmin(): boolean {
  return readCurrentUser()?.role?.name === "SUPER_ADMIN";
}

export function isLodgingOnlyClub(): boolean {
  // The lodging-only lock scopes a club's own admins to their vertical. A
  // SUPER_ADMIN spans every club, so locking them into /lodging while a
  // lodging club is their active club would strand them there — no club
  // switcher, no way back to the calendar. They keep the full chrome and only
  // *see* Lodging because their selected club is a lodging club.
  if (isSuperAdmin()) return false;
  if (!isLodgingClub()) return false;
  const clubId = readCurrentClubId();
  return clubId != null && LODGING_ONLY_CLUBS.includes(clubId);
}

/**
 * Routes a Lodging-only club may reach. Everything the admin needs lives
 * under /lodging; /signin and /partner remain reachable for auth and the
 * partner portal domain.
 */
const LODGING_ONLY_ALLOWED_PATHS = ["/lodging", "/signin", "/partner"];

export function isPathAllowedForLodgingOnly(pathname: string): boolean {
  return LODGING_ONLY_ALLOWED_PATHS.some(
    (p) => pathname === p || pathname.startsWith(p + "/")
  );
}
